// JobEditModal.jsx - updated
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  IconButton,
  Typography,
  Divider,
  alpha,
  useTheme,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPeopleGroup } from "@fortawesome/free-solid-svg-icons";
import { useSnackbar } from "notistack";
import { useQuery } from "react-query";
import { getScheduleJobDetails } from "../../../api/services/scheduling";
import ConfirmScheduleJob from "./ConfirmScheduleJob";
import ChangeJobTime from "../../../components/ScheduleJobAction/ChangeJobTime";
import CancelJobContent from "../../../components/HouseKeeping/modals/CancelJobModal";
import { QK } from "../../../base/qk";
import StaffManagementTab from "../StaffManagementTab";

const JobEditModal = ({ show, handleClose, JobId, status }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const { data: jobDetails, isLoading } = useQuery(
    ["job-details", JobId],
    () => getScheduleJobDetails(JobId),
    {
      enabled: !!JobId && show,
    }
  );

  useEffect(() => {
    if (show) setActiveTab(0);
  }, [show]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = () => {
    if (status === "cancelled") return theme.palette.error.main;
    if (status === "completed") return theme.palette.success.main;
    if (status === "in_progress") return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  const getStatusLabel = () => {
    if (status === "cancelled") return "Cancelled";
    if (status === "completed") return "Completed";
    if (status === "in_progress") return "In Progress";
    return "Scheduled";
  };

  const tabs = [
    { label: "Change Time", icon: <AccessTimeIcon />, value: 0 },
    { label: "Confirm Visit", icon: <CheckCircleIcon />, value: 1 },
    { label: "Staff Management", icon: <PeopleIcon />, value: 2 },
    { label: "Cancel Visit", icon: <CancelIcon />, value: 3 },
  ];

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleClose} maxWidth="md" fullWidth>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faPeopleGroup}
                style={{ color: theme.palette.primary.main, fontSize: 20 }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1A1A2E">
                Job Actions
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="#6B7280">
                  Manage schedule, staff & visit
                </Typography>
                <Chip
                  label={getStatusLabel()}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    bgcolor: alpha(getStatusColor(), 0.12),
                    color: getStatusColor(),
                  }}
                />
              </Stack>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        <Box sx={{ bgcolor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                minHeight: 48,
                color: "#64748B",
                "&.Mui-selected": {
                  color: "#2469F0",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                bgcolor: "#2469F0",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                sx={{
                  "&.Mui-selected": {
                    color: tab.value === 3 ? theme.palette.error.main : "#2469F0",
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 2.5, overflow: "auto", maxHeight: "calc(90vh - 130px)" }}>
          {activeTab === 0 && <ChangeJobTime id={JobId} onSuccess={handleClose} />}
          {activeTab === 1 && <ConfirmScheduleJob JobId={JobId} handleClose={handleClose} />}
          {activeTab === 2 && (
            <StaffManagementTab
              jobDetails={jobDetails}
              isLoading={isLoading}
              area={jobDetails?.sublocation}
              jobId={jobDetails?.id}
              assignmentId={jobDetails?.assignment}
            />
          )}
          {activeTab === 3 && (
            <CancelJobContent jobId={JobId} handleClose={handleClose} status={status} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default JobEditModal;